// =====================================================================
// FinanceiroClinica (F1+F2 / S58) — página /financas (Financeiro da Clínica).
// ⚠️ NÃO confundir com pages/Financeiro (Faturas/assinatura SaaS — intocada).
// Tabs: Contas a Receber · Orçamentos · Caixa (fluxo) · Contas a Pagar · Pagamentos.
// Multi-tenant: backend filtra por companyId do JWT (LGPD). MUI v4.
// =====================================================================
import React, { useState, useEffect, useCallback, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  CircularProgress,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Chip,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import PaymentIcon from "@material-ui/icons/Payment";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import ReceiptIcon from "@material-ui/icons/Receipt";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import WarningIcon from "@material-ui/icons/Warning";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import DoneAllIcon from "@material-ui/icons/DoneAll";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import { toast } from "react-toastify";
// RouterLink removido (S65): seu único uso era o botão "Abrir módulo Estoque" do placeholder da aba removida.

import {
  listReceivables,
  listQuotes,
  approveQuote,
  rejectQuote,
  cancelReceivable,
  listExpenses,
  payExpense,
  cancelExpense,
  reportsSummary,
  cashflow,
  listPayments,
  ensureReceipt,
  openReceiptPdf,
  openPaymentReceipt,
  fmtMoney,
  RECEIVABLE_STATUS,
  QUOTE_STATUS,
  EXPENSE_STATUS,
  EXPENSE_METHODS,
  PAYMENT_METHODS,
  financeErrorMessage,
  listStockProducts,
  stockAlerts,
  listCommissionRules,
  listCommissions,
  commissionsSummary,
  deleteCommissionRule,
  payCommission,
  reconcileCommissions,
  COMMISSION_STATUS,
  listAccounts,
  getAccountEntries,
  reconcileEntries,
  ACCOUNT_TYPES,
  listFiscalEmitentes,
  listFiscalNotes
} from "../../services/financeService";
import QuoteFormDialog from "./QuoteFormDialog";
import NfseCell from "./NfseCell";
import PaymentDialog from "./PaymentDialog";
import QuickPaymentDialog from "./QuickPaymentDialog";
import EditPaymentDialog from "./EditPaymentDialog"; // [S103] corrigir pagamento lançado
import FiscalEmitentesDialog from "./FiscalEmitentesDialog";
import ExpenseFormDialog from "./ExpenseFormDialog";
import StockProductDialog from "./StockProductDialog";
import StockMovementDialog from "./StockMovementDialog";
import CommissionRuleDialog from "./CommissionRuleDialog";
import AccountDialog from "./AccountDialog";
import TransferDialog from "./TransferDialog";
import AccountEntryDialog from "./AccountEntryDialog";
import ReconciliationTab from "./ReconciliationTab";
import CardsTab from "./CardsTab";
import MeloCommissionTab from "./MeloCommissionTab";
import MeloPainelTab from "./MeloPainelTab";
import { AuthContext } from "../../context/Auth/AuthContext";
import useClinicUnits from "../../hooks/useClinicUnits";
import UnitSelector from "../../components/UnitSelector";
import useIsMobile from "../../utils/useIsMobile";

const useStyles = makeStyles((theme) => ({
  mainPaper: { flex: 1, padding: theme.spacing(2), overflowY: "auto", minHeight: 0 },
  summary: { display: "flex", gap: theme.spacing(2), flexWrap: "wrap", marginBottom: theme.spacing(2) },
  card: {
    padding: theme.spacing(1.5, 2),
    minWidth: 150,
    flex: "1 1 140px",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8
  },
  cardValue: { fontWeight: 700, fontSize: "1.25rem" },
  toolbar: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
    margin: theme.spacing(1, 0, 2)
  },
  emptyState: { padding: theme.spacing(6), textAlign: "center", color: theme.palette.text.secondary },
  loadingWrap: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 180 },
  pagination: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2)
  }
}));

const PAGE_SIZE = 20;

const formatDateBR = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return "—";
  return `${d}/${m}/${y}`;
};

const StatusChip = ({ map, value, overdue }) => {
  const eff = overdue && (value === "pendente" || value === "parcial") ? "atrasado" : value;
  const cfg = (map && map[eff]) || { label: value, color: "#757575" };
  return (
    <Chip size="small" label={cfg.label} style={{ backgroundColor: cfg.color, color: "#fff", fontWeight: 600 }} />
  );
};

const MELO_COMPANY_ID = 3; // Instituto Melo (Pablo) — aba de comissão própria, gated.

const FinanceiroClinica = () => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { user } = useContext(AuthContext);
  const isMelo = Number(user && user.companyId) === MELO_COMPANY_ID;
  const isAdmin = !!(user && (user.profile === "admin" || user.super));
  // [S90] Unidades do Pablo (modelo B) — seletor de unidade + escopo das buscas.
  const { units, loading: unitsLoading, hasUnits, activeUnit, setActiveUnit } =
    useClinicUnits(user && user.companyId, isAdmin);
  // Pronto pra buscar: sabe se tem unidade E (se tem) já escolheu uma. Evita
  // request sem unitId numa company multi-unidade (403 pro não-admin).
  const scopeReady = !unitsLoading && (!hasUnits || activeUnit != null);
  const unitParam = hasUnits ? activeUnit : undefined; // number | 'all' | undefined
  // Ao CRIAR, a unidade tem que ser específica (Caixa Geral='all' → undefined → backend exige).
  const createUnitId = hasUnits && activeUnit !== "all" ? activeUnit : undefined;

  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState({
    recebidoMes: 0, pagoMes: 0, saldoMes: 0, aReceber: 0, receberAtrasado: 0, aPagar: 0, pagarVencido: 0
  });

  // Contas a receber
  const [recvFilter, setRecvFilter] = useState("open");
  const [receivables, setReceivables] = useState([]);
  const [recvCount, setRecvCount] = useState(0);
  const [recvPage, setRecvPage] = useState(1);
  const [recvLoading, setRecvLoading] = useState(false);

  // Orçamentos
  const [quotes, setQuotes] = useState([]);
  const [quoteCount, setQuoteCount] = useState(0);
  const [quotePage, setQuotePage] = useState(1);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Caixa
  const [cf, setCf] = useState(null);
  const [cfLoading, setCfLoading] = useState(false);

  // Contas a pagar
  const [expFilter, setExpFilter] = useState("open");
  const [expenses, setExpenses] = useState([]);
  const [expCount, setExpCount] = useState(0);
  const [expPage, setExpPage] = useState(1);
  const [expLoading, setExpLoading] = useState(false);

  // Pagamentos
  const [payments, setPayments] = useState([]);
  const [payCount, setPayCount] = useState(0);
  const [canEditPayments, setCanEditPayments] = useState(false); // [S103] flag do backend (gated por tenant)
  const [editPayTarget, setEditPayTarget] = useState(null);      // [S103] pagamento sendo corrigido
  const [payPage, setPayPage] = useState(1);
  const [payLoading, setPayLoading] = useState(false);
  // [S97] Fiscal (NF-e/NFS-e Focus) — gating (coluna NF só se houver emitente) + mapa paymentId->nota
  const [nfEmitentes, setNfEmitentes] = useState([]);
  const [nfNotes, setNfNotes] = useState({});
  const [fiscalDlgOpen, setFiscalDlgOpen] = useState(false);

  // Estoque (F2-ext)
  const [stockSearch, setStockSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [stockAlertsData, setStockAlertsData] = useState({ lowStock: [], expiring: [] });
  const [stockLoading, setStockLoading] = useState(false);

  // Comissões (F2-ext)
  const [commRules, setCommRules] = useState([]);
  const [commProfessionals, setCommProfessionals] = useState([]);
  const [commList, setCommList] = useState([]);
  const [commSummary, setCommSummary] = useState([]);
  const [commFrom, setCommFrom] = useState("");
  const [commTo, setCommTo] = useState("");
  const [commLoading, setCommLoading] = useState(false);

  // Tesouraria (S63)
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [accountEdit, setAccountEdit] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [extratoAccountId, setExtratoAccountId] = useState("");
  const [extrato, setExtrato] = useState(null);
  const [extFrom, setExtFrom] = useState("");
  const [extTo, setExtTo] = useState("");
  const [extLoading, setExtLoading] = useState(false);
  const [expPayAccountId, setExpPayAccountId] = useState("");

  // Dialogs
  const [payTarget, setPayTarget] = useState(null);
  const [quickPayOpen, setQuickPayOpen] = useState(false); // [S91] recebimento rápido de balcão
  const [quoteFormOpen, setQuoteFormOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [installments, setInstallments] = useState(1);
  const [firstDueDate, setFirstDueDate] = useState("");
  const [expenseFormOpen, setExpenseFormOpen] = useState(false);
  const [expPayTarget, setExpPayTarget] = useState(null);
  const [expPayMethod, setExpPayMethod] = useState("dinheiro");
  const [stockProductOpen, setStockProductOpen] = useState(false);
  const [stockProductEdit, setStockProductEdit] = useState(null);
  const [stockMoveTarget, setStockMoveTarget] = useState(null);
  const [commRuleOpen, setCommRuleOpen] = useState(false);
  const [commRuleEdit, setCommRuleEdit] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!scopeReady) return;
    try { setSummary(await reportsSummary(unitParam)); } catch (e) { /* secundário */ }
  }, [scopeReady, unitParam]);

  const fetchReceivables = useCallback(async (page = 1) => {
    if (!scopeReady) return;
    setRecvLoading(true);
    try {
      const params = { page, pageSize: PAGE_SIZE, unitId: unitParam };
      if (recvFilter === "open") params.status = "pendente";
      else if (recvFilter === "overdue") params.due = "overdue";
      const data = await listReceivables(params);
      setReceivables(Array.isArray(data?.receivables) ? data.receivables : []);
      setRecvCount(Number(data?.count) || 0);
      setRecvPage(page);
    } catch (e) {
      toast.error("Falha ao carregar contas a receber.");
      setReceivables([]); setRecvCount(0);
    } finally { setRecvLoading(false); }
  }, [recvFilter, scopeReady, unitParam]);

  const fetchQuotes = useCallback(async (page = 1) => {
    if (!scopeReady) return;
    setQuoteLoading(true);
    try {
      const data = await listQuotes({ page, pageSize: PAGE_SIZE, unitId: unitParam });
      setQuotes(Array.isArray(data?.quotes) ? data.quotes : []);
      setQuoteCount(Number(data?.count) || 0);
      setQuotePage(page);
    } catch (e) {
      toast.error("Falha ao carregar orçamentos.");
      setQuotes([]); setQuoteCount(0);
    } finally { setQuoteLoading(false); }
  }, [scopeReady, unitParam]);

  const fetchCashflow = useCallback(async () => {
    if (!scopeReady) return;
    setCfLoading(true);
    try { setCf(await cashflow({ unitId: unitParam })); }
    catch (e) { toast.error("Falha ao carregar o caixa."); setCf(null); }
    finally { setCfLoading(false); }
  }, [scopeReady, unitParam]);

  const fetchExpenses = useCallback(async (page = 1) => {
    if (!scopeReady) return;
    setExpLoading(true);
    try {
      const params = { page, pageSize: PAGE_SIZE, unitId: unitParam };
      if (expFilter === "open") params.status = "pendente";
      else if (expFilter === "overdue") params.due = "overdue";
      else if (expFilter === "paid") params.status = "pago";
      const data = await listExpenses(params);
      setExpenses(Array.isArray(data?.expenses) ? data.expenses : []);
      setExpCount(Number(data?.count) || 0);
      setExpPage(page);
    } catch (e) {
      toast.error("Falha ao carregar contas a pagar.");
      setExpenses([]); setExpCount(0);
    } finally { setExpLoading(false); }
  }, [expFilter, scopeReady, unitParam]);

  // [S97] carrega os emitentes fiscais (gating da coluna NF + reload após cadastro).
  const reloadNfEmitentes = useCallback(async () => {
    try {
      const d = await listFiscalEmitentes();
      setNfEmitentes(Array.isArray(d?.emitentes) ? d.emitentes : []);
    } catch (e) { setNfEmitentes([]); }
  }, []);
  useEffect(() => { reloadNfEmitentes(); }, [reloadNfEmitentes]);
  // [S97] recarrega o mapa payment -> nota fiscal (chamado após emitir/atualizar).
  const reloadNfNotes = useCallback(async () => {
    try {
      const nd = await listFiscalNotes();
      const map = {};
      (nd?.notes || []).forEach((n) => { if (!map[n.paymentId]) map[n.paymentId] = n; });
      setNfNotes(map);
    } catch (e) { /* silencioso */ }
  }, []);

  const fetchPayments = useCallback(async (page = 1) => {
    if (!scopeReady) return;
    setPayLoading(true);
    try {
      const data = await listPayments({ page, pageSize: PAGE_SIZE, unitId: unitParam });
      setPayments(Array.isArray(data?.payments) ? data.payments : []);
      setPayCount(Number(data?.count) || 0);
      setCanEditPayments(data?.canEdit === true); // [S103] mostra o ✏️ só pro tenant habilitado
      setPayPage(page);
      reloadNfNotes();
    } catch (e) {
      toast.error("Falha ao carregar pagamentos.");
      setPayments([]); setPayCount(0);
    } finally { setPayLoading(false); }
  }, [scopeReady, unitParam, reloadNfNotes]);

  const fetchStock = useCallback(async () => {
    if (!scopeReady) return;
    setStockLoading(true);
    try {
      const [data, al] = await Promise.all([
        listStockProducts({ search: stockSearch || undefined, unitId: unitParam, pageSize: 200 }),
        stockAlerts(unitParam)
      ]);
      setProducts(Array.isArray(data?.products) ? data.products : []);
      setStockAlertsData({ lowStock: al?.lowStock || [], expiring: al?.expiring || [] });
    } catch (e) {
      toast.error("Falha ao carregar o estoque.");
      setProducts([]); setStockAlertsData({ lowStock: [], expiring: [] });
    } finally { setStockLoading(false); }
  }, [stockSearch, scopeReady, unitParam]);

  const fetchCommissions = useCallback(async () => {
    setCommLoading(true);
    try {
      const period = { from: commFrom || undefined, to: commTo || undefined };
      const [rulesData, sum, listData] = await Promise.all([
        listCommissionRules(),
        commissionsSummary(period),
        listCommissions({ ...period, pageSize: 200 })
      ]);
      setCommRules(rulesData?.rules || []);
      setCommProfessionals(rulesData?.professionals || []);
      setCommSummary(sum?.byProfessional || []);
      setCommList(listData?.commissions || []);
    } catch (e) {
      toast.error("Falha ao carregar comissões.");
    } finally { setCommLoading(false); }
  }, [commFrom, commTo]);

  const fetchAccounts = useCallback(async () => {
    if (!scopeReady) return;
    setAccountsLoading(true);
    try {
      const data = await listAccounts(unitParam);
      const list = Array.isArray(data?.accounts) ? data.accounts : [];
      setAccounts(list);
      setExtratoAccountId((prev) => prev || String((list.find((a) => a.isDefault) || list[0] || {}).id || ""));
    } catch (e) {
      toast.error("Falha ao carregar contas.");
    } finally { setAccountsLoading(false); }
  }, [scopeReady, unitParam]);

  const fetchEntries = useCallback(async () => {
    if (!extratoAccountId) { setExtrato(null); return; }
    setExtLoading(true);
    try {
      setExtrato(await getAccountEntries(extratoAccountId, { from: extFrom || undefined, to: extTo || undefined }));
    } catch (e) {
      toast.error("Falha ao carregar o extrato."); setExtrato(null);
    } finally { setExtLoading(false); }
  }, [extratoAccountId, extFrom, extTo]);

  const handleReconcileEntries = async () => {
    try {
      const r = await reconcileEntries();
      const cb = r.cardsBackfilled ? ` + ${r.cardsBackfilled} cartão(ões) ao caixa` : "";
      toast.success(`Extrato reapurado: ${r.credits} crédito(s) + ${r.debits} débito(s)${cb}.`);
      fetchAccounts(); fetchEntries(); fetchSummary();
    } catch (err) { toast.error(financeErrorMessage(err)); }
  };

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => { if (tab === 0) fetchReceivables(1); }, [tab, fetchReceivables]);
  useEffect(() => { if (tab === 1) fetchQuotes(1); }, [tab, fetchQuotes]);
  useEffect(() => { if (tab === 2) fetchCashflow(); }, [tab, fetchCashflow]);
  useEffect(() => { if (tab === 3) fetchExpenses(1); }, [tab, fetchExpenses]);
  useEffect(() => { if (tab === 4) fetchPayments(1); }, [tab, fetchPayments]);
  useEffect(() => { if (tab === 5) fetchStock(); }, [tab, fetchStock]);
  useEffect(() => { if (tab === 6) fetchCommissions(); }, [tab, fetchCommissions]);
  useEffect(() => { if (tab === 8 && extratoAccountId) fetchEntries(); }, [tab, extratoAccountId, fetchEntries]);
  useEffect(() => {
    if (expPayTarget) setExpPayAccountId(String((accounts.find((a) => a.isDefault) || accounts[0] || {}).id || ""));
  }, [expPayTarget, accounts]);

  const handlePaid = () => {
    setPayTarget(null);
    fetchSummary();
    fetchReceivables(recvPage);
    toast.success("Pagamento registrado.");
  };

  const handleApprove = async () => {
    try {
      await approveQuote(approveTarget.id, {
        installments: Number(installments) || 1,
        firstDueDate: firstDueDate || undefined
      });
      toast.success("Orçamento aprovado — parcelas geradas em Contas a Receber.");
      setApproveTarget(null); setInstallments(1); setFirstDueDate("");
      fetchQuotes(quotePage); fetchSummary();
    } catch (err) { toast.error(financeErrorMessage(err)); }
  };

  const handleReject = async (q) => {
    try { await rejectQuote(q.id); fetchQuotes(quotePage); }
    catch (err) { toast.error(financeErrorMessage(err)); }
  };

  const handleCancelReceivable = async (r) => {
    try { await cancelReceivable(r.id); fetchReceivables(recvPage); fetchSummary(); }
    catch (err) { toast.error(financeErrorMessage(err)); }
  };

  const handlePayExpense = async () => {
    try {
      await payExpense(expPayTarget.id, { method: expPayMethod, accountId: expPayAccountId || undefined });
      toast.success("Despesa paga.");
      setExpPayTarget(null); setExpPayMethod("dinheiro");
      fetchExpenses(expPage); fetchSummary();
    } catch (err) { toast.error(financeErrorMessage(err)); }
  };

  const handleCancelExpense = async (e) => {
    try { await cancelExpense(e.id); fetchExpenses(expPage); fetchSummary(); }
    catch (err) { toast.error(financeErrorMessage(err)); }
  };

  const handleReceipt = async (paymentId) => {
    try {
      const r = await ensureReceipt(paymentId);
      await openReceiptPdf(r.id);
    } catch (err) { toast.error(financeErrorMessage(err)); }
  };

  const handleDeleteRule = async (r) => {
    try { await deleteCommissionRule(r.id); fetchCommissions(); }
    catch (err) { toast.error(financeErrorMessage(err)); }
  };

  const handlePayCommission = async (c) => {
    try { await payCommission(c.id); fetchCommissions(); }
    catch (err) { toast.error(financeErrorMessage(err)); }
  };

  const handleReconcile = async () => {
    try {
      const r = await reconcileCommissions();
      toast.success(`Reapuração concluída: ${r.accrued} nova(s) comissão(ões).`);
      fetchCommissions();
    } catch (err) { toast.error(financeErrorMessage(err)); }
  };

  // [S95] Abre o comprovante da maquininha (foto/PDF) anexado ao pagamento.
  // Reusa openPaymentReceipt (GET /finance/payments/:id/comprovante, já existe S91).
  const handleOpenComprovante = async (paymentId) => {
    try {
      await openPaymentReceipt(paymentId);
    } catch (e) {
      toast.error(financeErrorMessage(e) || "Não foi possível abrir o comprovante.");
    }
  };

  const methodLabel = (m) => (PAYMENT_METHODS.find((x) => x.value === m) || {}).label || m;

  const accTypeLabel = (t) => (ACCOUNT_TYPES.find((x) => x.value === t) || {}).label || t;
  const SOURCE_LABEL = { payment: "Recebimento", expense: "Despesa", transfer: "Transferência", manual: "Manual", adjustment: "Ajuste" };

  const recvTotalPages = Math.max(Math.ceil(recvCount / PAGE_SIZE), 1);
  const quoteTotalPages = Math.max(Math.ceil(quoteCount / PAGE_SIZE), 1);
  const expTotalPages = Math.max(Math.ceil(expCount / PAGE_SIZE), 1);
  const payTotalPages = Math.max(Math.ceil(payCount / PAGE_SIZE), 1);

  return (
    <MainContainer>
      <MainHeader>
        <Title>Financeiro da Clínica</Title>
        <MainHeaderButtonsWrapper>
          {/* [S91] Recebimento rápido de balcão — SEMPRE visível (1 clique, qualquer aba). */}
          <Button variant="contained" startIcon={<PaymentIcon />} onClick={() => setQuickPayOpen(true)}
            style={{ backgroundColor: "#2e7d32", color: "#fff", marginRight: 8 }}>
            Registrar recebimento
          </Button>
          {tab === 3 ? (
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setExpenseFormOpen(true)}>
              Nova Despesa
            </Button>
          ) : tab === 6 ? (
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => { setCommRuleEdit(null); setCommRuleOpen(true); }}>
              Nova Regra
            </Button>
          ) : tab === 7 ? (
            <>
              <Button onClick={() => setTransferOpen(true)} disabled={accounts.filter((a) => a.active).length < 2}>Transferir</Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => { setAccountEdit(null); setAccountDialogOpen(true); }}>Nova Conta</Button>
            </>
          ) : tab === 8 ? (
            <>
              <Button onClick={handleReconcileEntries}>Reapurar</Button>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setEntryDialogOpen(true)} disabled={accounts.length === 0}>Lançamento</Button>
            </>
          ) : tab === 9 || tab === 10 || tab === 11 || tab === 12 ? (
            <span />
          ) : (
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setQuoteFormOpen(true)}>
              Novo Orçamento
            </Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* [S90] seletor de unidade (só aparece p/ companies com clinic_units) */}
        <UnitSelector units={units} value={activeUnit} onChange={setActiveUnit} isAdmin={isAdmin} />
        {/* Resumo do dono */}
        <div className={classes.summary}>
          <div className={classes.card}>
            <Typography variant="caption" color="textSecondary">Recebido (mês)</Typography>
            <Typography className={classes.cardValue} style={{ color: "#2e7d32" }}>{fmtMoney(summary.recebidoMes)}</Typography>
          </div>
          <div className={classes.card}>
            <Typography variant="caption" color="textSecondary">Saldo (mês)</Typography>
            <Typography className={classes.cardValue} style={{ color: summary.saldoMes < 0 ? "#c62828" : undefined }}>{fmtMoney(summary.saldoMes)}</Typography>
          </div>
          <div className={classes.card}>
            <Typography variant="caption" color="textSecondary">A receber</Typography>
            <Typography className={classes.cardValue}>{fmtMoney(summary.aReceber)}</Typography>
            {summary.receberAtrasado > 0 && (
              <Typography variant="caption" style={{ color: "#c62828" }}>{fmtMoney(summary.receberAtrasado)} atrasado</Typography>
            )}
          </div>
          <div className={classes.card}>
            <Typography variant="caption" color="textSecondary">A pagar</Typography>
            <Typography className={classes.cardValue}>{fmtMoney(summary.aPagar)}</Typography>
            {summary.pagarVencido > 0 && (
              <Typography variant="caption" style={{ color: "#c62828" }}>{fmtMoney(summary.pagarVencido)} vencido</Typography>
            )}
          </div>
        </div>

        {/* Abas com value EXPLÍCITO: a "Estoque" (value 5) virou módulo próprio em /estoque (S64) e foi
            removida daqui. O value 5 fica vago de propósito → Comissões/Contas/Extrato/Conciliação/Cartões
            mantêm 6–10 e nenhum painel/useEffect/setTab precisa renumerar (REGRA #6). */}
        <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary" variant="scrollable">
          <Tab label="Contas a Receber" value={0} />
          <Tab label="Orçamentos" value={1} />
          <Tab label="Caixa" value={2} />
          <Tab label="Contas a Pagar" value={3} />
          <Tab label="Pagamentos" value={4} />
          {!isMelo && <Tab label="Comissões" value={6} />}
          <Tab label="Contas" value={7} />
          <Tab label="Extrato" value={8} />
          <Tab label="Conciliação" value={9} />
          <Tab label="Cartões" value={10} />
          {isMelo && <Tab label="Comissão (Pablo)" value={11} />}
          {isMelo && <Tab label="Painel (Pablo)" value={12} />}
        </Tabs>

        {/* TAB 0 — Contas a receber */}
        {tab === 0 && (
          <>
            <div className={classes.toolbar}>
              <TextField select size="small" variant="outlined" label="Filtro" value={recvFilter}
                onChange={(e) => setRecvFilter(e.target.value)} style={{ minWidth: 180 }}>
                <MenuItem value="open">Em aberto</MenuItem>
                <MenuItem value="overdue">Atrasadas</MenuItem>
                <MenuItem value="all">Todas</MenuItem>
              </TextField>
              <Box flexGrow={1} />
              <Typography variant="body2" color="textSecondary">{recvCount} conta(s)</Typography>
            </div>
            {recvLoading ? <div className={classes.loadingWrap}><CircularProgress /></div>
              : receivables.length === 0 ? <div className={classes.emptyState}><Typography>Nenhuma conta a receber neste filtro.</Typography></div>
              : (
                <>
                  <div style={{ overflowX: "auto", width: "100%" }}>
                  <Table size="small" style={{ minWidth: 640 }}>
                    <TableHead><TableRow>
                      <TableCell>Paciente</TableCell><TableCell>Descrição</TableCell><TableCell>Vencimento</TableCell>
                      <TableCell align="right">Valor</TableCell><TableCell align="right">Saldo</TableCell>
                      <TableCell align="center">Status</TableCell><TableCell align="center" style={{ width: 110 }}>Ações</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {receivables.map((r) => {
                        const open = r.status === "pendente" || r.status === "parcial";
                        return (
                          <TableRow key={r.id} hover>
                            <TableCell>{r.patient?.fullName || "—"}</TableCell>
                            <TableCell>{r.description || "—"}</TableCell>
                            <TableCell>{formatDateBR(r.dueDate)}</TableCell>
                            <TableCell align="right">{fmtMoney(r.amount)}</TableCell>
                            <TableCell align="right">{fmtMoney(r.remaining)}</TableCell>
                            <TableCell align="center"><StatusChip map={RECEIVABLE_STATUS} value={r.status} overdue={r.isOverdue} /></TableCell>
                            <TableCell align="center">
                              {open && (
                                <>
                                  <Tooltip title="Registrar pagamento"><IconButton size="small" color="primary" onClick={() => setPayTarget(r)}><PaymentIcon fontSize="small" /></IconButton></Tooltip>
                                  {Number(r.paidAmount) === 0 && (
                                    <Tooltip title="Cancelar conta"><IconButton size="small" onClick={() => handleCancelReceivable(r)}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                                  )}
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </div>
                  <div className={classes.pagination}>
                    <Button size="small" disabled={recvPage <= 1} onClick={() => fetchReceivables(recvPage - 1)}>Anterior</Button>
                    <Typography variant="body2">Página {recvPage} de {recvTotalPages}</Typography>
                    <Button size="small" disabled={recvPage >= recvTotalPages} onClick={() => fetchReceivables(recvPage + 1)}>Próxima</Button>
                  </div>
                </>
              )}
          </>
        )}

        {/* TAB 1 — Orçamentos */}
        {tab === 1 && (
          <>
            <div className={classes.toolbar}>
              <Box flexGrow={1} />
              <Typography variant="body2" color="textSecondary">{quoteCount} orçamento(s)</Typography>
            </div>
            {quoteLoading ? <div className={classes.loadingWrap}><CircularProgress /></div>
              : quotes.length === 0 ? <div className={classes.emptyState}><Typography>Nenhum orçamento ainda. Clique em "Novo Orçamento".</Typography></div>
              : (
                <>
                  <div style={{ overflowX: "auto", width: "100%" }}>
                  <Table size="small" style={{ minWidth: 640 }}>
                    <TableHead><TableRow>
                      <TableCell>Paciente</TableCell><TableCell>Data</TableCell><TableCell align="right">Total</TableCell>
                      <TableCell align="center">Status</TableCell><TableCell align="center" style={{ width: 120 }}>Ações</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {quotes.map((q) => {
                        const editable = q.status === "rascunho" || q.status === "enviado";
                        return (
                          <TableRow key={q.id} hover>
                            <TableCell>{q.patient?.fullName || "—"}</TableCell>
                            <TableCell>{formatDateBR(q.createdAt)}</TableCell>
                            <TableCell align="right">{fmtMoney(q.total)}</TableCell>
                            <TableCell align="center"><StatusChip map={QUOTE_STATUS} value={q.status} /></TableCell>
                            <TableCell align="center">
                              {editable && (
                                <>
                                  <Tooltip title="Aprovar (gera contas a receber)"><IconButton size="small" color="primary" onClick={() => setApproveTarget(q)}><CheckCircleIcon fontSize="small" /></IconButton></Tooltip>
                                  <Tooltip title="Recusar"><IconButton size="small" onClick={() => handleReject(q)}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </div>
                  <div className={classes.pagination}>
                    <Button size="small" disabled={quotePage <= 1} onClick={() => fetchQuotes(quotePage - 1)}>Anterior</Button>
                    <Typography variant="body2">Página {quotePage} de {quoteTotalPages}</Typography>
                    <Button size="small" disabled={quotePage >= quoteTotalPages} onClick={() => fetchQuotes(quotePage + 1)}>Próxima</Button>
                  </div>
                </>
              )}
          </>
        )}

        {/* TAB 2 — Caixa (fluxo) */}
        {tab === 2 && (
          <>
            {cfLoading ? <div className={classes.loadingWrap}><CircularProgress /></div>
              : !cf ? <div className={classes.emptyState}><Typography>Sem dados de caixa.</Typography></div>
              : (
                <>
                  <div className={classes.toolbar}>
                    <Typography variant="body2" color="textSecondary">
                      Período: {formatDateBR(cf.from)} a {formatDateBR(cf.to)}
                    </Typography>
                    <Box flexGrow={1} />
                    <Chip label={`Entradas ${fmtMoney(cf.totalEntradas)}`} style={{ backgroundColor: "#2e7d32", color: "#fff" }} />
                    <Chip label={`Saídas ${fmtMoney(cf.totalSaidas)}`} style={{ backgroundColor: "#c62828", color: "#fff" }} />
                    <Chip label={`Saldo ${fmtMoney(cf.saldo)}`} style={{ backgroundColor: cf.saldo < 0 ? "#c62828" : "#1565c0", color: "#fff" }} />
                  </div>
                  {cf.days.length === 0 ? <div className={classes.emptyState}><Typography>Nenhuma movimentação no período.</Typography></div>
                    : (
                      <div style={{ overflowX: "auto", width: "100%" }}>
                      <Table size="small">
                        <TableHead><TableRow>
                          <TableCell>Dia</TableCell><TableCell align="right">Entradas</TableCell>
                          <TableCell align="right">Saídas</TableCell><TableCell align="right">Saldo</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                          {cf.days.map((d) => (
                            <TableRow key={d.dia}>
                              <TableCell>{formatDateBR(d.dia)}</TableCell>
                              <TableCell align="right" style={{ color: "#2e7d32" }}>{fmtMoney(d.entradas)}</TableCell>
                              <TableCell align="right" style={{ color: "#c62828" }}>{fmtMoney(d.saidas)}</TableCell>
                              <TableCell align="right">{fmtMoney(d.saldo)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                    )}
                </>
              )}
          </>
        )}

        {/* TAB 3 — Contas a pagar */}
        {tab === 3 && (
          <>
            <div className={classes.toolbar}>
              <TextField select size="small" variant="outlined" label="Filtro" value={expFilter}
                onChange={(e) => setExpFilter(e.target.value)} style={{ minWidth: 180 }}>
                <MenuItem value="open">Em aberto</MenuItem>
                <MenuItem value="overdue">Vencidas</MenuItem>
                <MenuItem value="paid">Pagas</MenuItem>
                <MenuItem value="all">Todas</MenuItem>
              </TextField>
              <Box flexGrow={1} />
              <Typography variant="body2" color="textSecondary">{expCount} conta(s)</Typography>
            </div>
            {expLoading ? <div className={classes.loadingWrap}><CircularProgress /></div>
              : expenses.length === 0 ? <div className={classes.emptyState}><Typography>Nenhuma despesa neste filtro. Clique em "Nova Despesa".</Typography></div>
              : (
                <>
                  <div style={{ overflowX: "auto", width: "100%" }}>
                  <Table size="small" style={{ minWidth: 640 }}>
                    <TableHead><TableRow>
                      <TableCell>Descrição</TableCell><TableCell>Categoria</TableCell><TableCell>Fornecedor</TableCell>
                      <TableCell>Vencimento</TableCell><TableCell align="right">Valor</TableCell>
                      <TableCell align="center">Status</TableCell><TableCell align="center" style={{ width: 110 }}>Ações</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {expenses.map((e) => (
                        <TableRow key={e.id} hover>
                          <TableCell>{e.description || "—"}</TableCell>
                          <TableCell>{e.category || "—"}</TableCell>
                          <TableCell>{e.supplier || "—"}</TableCell>
                          <TableCell>{formatDateBR(e.dueDate)}</TableCell>
                          <TableCell align="right">{fmtMoney(e.amount)}</TableCell>
                          <TableCell align="center"><StatusChip map={EXPENSE_STATUS} value={e.status} overdue={e.isOverdue} /></TableCell>
                          <TableCell align="center">
                            {e.status === "pendente" && (
                              <>
                                <Tooltip title="Pagar"><IconButton size="small" color="primary" onClick={() => setExpPayTarget(e)}><PaymentIcon fontSize="small" /></IconButton></Tooltip>
                                <Tooltip title="Cancelar"><IconButton size="small" onClick={() => handleCancelExpense(e)}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                  <div className={classes.pagination}>
                    <Button size="small" disabled={expPage <= 1} onClick={() => fetchExpenses(expPage - 1)}>Anterior</Button>
                    <Typography variant="body2">Página {expPage} de {expTotalPages}</Typography>
                    <Button size="small" disabled={expPage >= expTotalPages} onClick={() => fetchExpenses(expPage + 1)}>Próxima</Button>
                  </div>
                </>
              )}
          </>
        )}

        {/* TAB 4 — Pagamentos (histórico + recibo) */}
        {tab === 4 && (
          <>
            <div className={classes.toolbar}>
              <Button size="small" variant="outlined" color="primary" onClick={() => setFiscalDlgOpen(true)}
                style={{ textTransform: "none", fontWeight: 600, borderRadius: 8 }}>🧾 Emitentes de NF</Button>
              <Box flexGrow={1} />
              <Typography variant="body2" color="textSecondary">{payCount} pagamento(s)</Typography>
            </div>
            {payLoading ? <div className={classes.loadingWrap}><CircularProgress /></div>
              : payments.length === 0 ? <div className={classes.emptyState}><Typography>Nenhum pagamento registrado ainda.</Typography></div>
              : (
                <>
                  <div style={{ overflowX: "auto", width: "100%" }}>
                  <Table size="small" style={{ minWidth: 640 }}>
                    <TableHead><TableRow>
                      <TableCell>Data</TableCell><TableCell>Paciente</TableCell><TableCell>Descrição</TableCell>
                      <TableCell align="right">Valor</TableCell><TableCell>Forma</TableCell>
                      <TableCell align="center" style={{ width: 120 }}>Recibo / Anexo</TableCell>
                      {nfEmitentes.length > 0 && <TableCell align="center" style={{ width: 160 }}>NF</TableCell>}
                    </TableRow></TableHead>
                    <TableBody>
                      {payments.map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell>{formatDateBR(p.paidAt)}</TableCell>
                          <TableCell>
                            {p.patient?.fullName || "—"}
                            {p.payerName ? (
                              <Typography variant="caption" display="block" color="textSecondary">
                                pago por: {p.payerName}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell style={{ maxWidth: 340, whiteSpace: "normal", wordBreak: "break-word" }}>
                            {p.notes || "—"}
                          </TableCell>
                          <TableCell align="right">
                            {fmtMoney(p.amount)}
                            {p.installmentsTotal > 1 ? (
                              <Typography variant="caption" display="block" color="textSecondary">
                                {p.installmentsTotal}× de {fmtMoney(p.amount / p.installmentsTotal)}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>{methodLabel(p.method)}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Abrir recibo"><IconButton size="small" color="primary" onClick={() => handleReceipt(p.id)}><ReceiptIcon fontSize="small" /></IconButton></Tooltip>
                            {p.receiptPath ? (
                              <Tooltip title="Ver comprovante anexado (maquininha)"><IconButton size="small" style={{ color: "#2e7d32" }} onClick={() => handleOpenComprovante(p.id)}><AttachFileIcon fontSize="small" /></IconButton></Tooltip>
                            ) : null}
                            {canEditPayments ? (
                              <Tooltip title="Corrigir valor / forma / anexo"><IconButton size="small" style={{ color: "#616161" }} onClick={() => setEditPayTarget(p)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                            ) : null}
                          </TableCell>
                          {nfEmitentes.length > 0 && (
                            <TableCell align="center">
                              <NfseCell payment={p} note={nfNotes[p.id]} onChanged={reloadNfNotes} />
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                  <div className={classes.pagination}>
                    <Button size="small" disabled={payPage <= 1} onClick={() => fetchPayments(payPage - 1)}>Anterior</Button>
                    <Typography variant="body2">Página {payPage} de {payTotalPages}</Typography>
                    <Button size="small" disabled={payPage >= payTotalPages} onClick={() => fetchPayments(payPage + 1)}>Próxima</Button>
                  </div>
                </>
              )}
          </>
        )}

        {/* Aba "Estoque" (value 5) REMOVIDA da UI — virou módulo próprio em /estoque (S64).
            O bloco {false && ...} abaixo é o código antigo, mantido INERTE de propósito: não
            renderiza nada (false) e mantém o state/diálogos de estoque referenciados. */}
        {false && (
          <>
            {(stockAlertsData.lowStock.length > 0 || stockAlertsData.expiring.length > 0) && (
              <Box mt={1} mb={1} display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
                {stockAlertsData.lowStock.map((a) => (
                  <Chip key={`low-${a.id}`} size="small" icon={<WarningIcon style={{ color: "#fff" }} />}
                    label={`${a.name}: ${Number(a.currentQty)} ${a.unit || ""} (mín ${Number(a.minQty)})`}
                    style={{ backgroundColor: "#c62828", color: "#fff" }} />
                ))}
                {stockAlertsData.expiring.map((a, i) => (
                  <Chip key={`exp-${i}`} size="small" icon={<WarningIcon style={{ color: "#fff" }} />}
                    label={`${a.name} • lote ${a.lot || "—"} • ${a.expired ? "VENCIDO" : "vence"} ${formatDateBR(a.expiry)}`}
                    style={{ backgroundColor: a.expired ? "#757575" : "#b26a00", color: "#fff" }} />
                ))}
              </Box>
            )}
            <div className={classes.toolbar}>
              <TextField size="small" variant="outlined" label="Buscar produto" value={stockSearch}
                onChange={(e) => setStockSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") fetchStock(); }} style={{ minWidth: 240 }} />
              <Button size="small" onClick={() => fetchStock()}>Buscar</Button>
              <Box flexGrow={1} />
              <Typography variant="body2" color="textSecondary">{products.length} produto(s)</Typography>
            </div>
            {stockLoading ? <div className={classes.loadingWrap}><CircularProgress /></div>
              : products.length === 0 ? <div className={classes.emptyState}><Typography>Nenhum produto. Clique em "Novo Produto".</Typography></div>
              : (
                <div style={{ overflowX: "auto", width: "100%" }}>
                <Table size="small" style={{ minWidth: 640 }}>
                  <TableHead><TableRow>
                    <TableCell>Produto</TableCell><TableCell>Categoria</TableCell>
                    <TableCell align="right">Saldo</TableCell><TableCell align="right">Mínimo</TableCell>
                    <TableCell align="center" style={{ width: 110 }}>Ações</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {products.map((p) => {
                      const low = Number(p.minQty) > 0 && Number(p.currentQty) <= Number(p.minQty);
                      return (
                        <TableRow key={p.id} hover>
                          <TableCell>
                            {p.name}
                            {!p.active && <Chip size="small" label="inativo" style={{ marginLeft: 6 }} />}
                          </TableCell>
                          <TableCell>{p.category || "—"}</TableCell>
                          <TableCell align="right" style={{ color: low ? "#c62828" : undefined, fontWeight: low ? 700 : 400 }}>
                            {Number(p.currentQty)} {p.unit || ""}
                          </TableCell>
                          <TableCell align="right">{Number(p.minQty) ? Number(p.minQty) : "—"}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Movimentar (entrada/saída/ajuste)"><IconButton size="small" color="primary" onClick={() => setStockMoveTarget(p)}><SwapHorizIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Editar"><IconButton size="small" onClick={() => { setStockProductEdit(p); setStockProductOpen(true); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </div>
              )}
          </>
        )}

        {/* TAB 6 — Comissões */}
        {tab === 6 && (
          <>
            <div className={classes.toolbar}>
              <TextField type="date" size="small" variant="outlined" label="De" value={commFrom}
                onChange={(e) => setCommFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField type="date" size="small" variant="outlined" label="Até" value={commTo}
                onChange={(e) => setCommTo(e.target.value)} InputLabelProps={{ shrink: true }} />
              <Button size="small" variant="outlined" onClick={() => fetchCommissions()}>Filtrar</Button>
              <Box flexGrow={1} />
              <Tooltip title="Apura comissões de pagamentos ainda sem comissão (após criar regra)">
                <Button size="small" onClick={handleReconcile}>Reapurar</Button>
              </Tooltip>
            </div>
            {commLoading ? <div className={classes.loadingWrap}><CircularProgress /></div>
              : (
                <>
                  <Typography variant="subtitle2" gutterBottom>A pagar por profissional (período)</Typography>
                  {commSummary.length === 0 ? <Typography variant="body2" color="textSecondary" paragraph>Nenhuma comissão apurada no período.</Typography>
                    : (
                      <Box display="flex" style={{ gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                        {commSummary.map((s) => (
                          <div key={s.professionalId} className={classes.card}>
                            <Typography variant="caption" color="textSecondary">{s.professionalName || "—"}</Typography>
                            <Typography className={classes.cardValue} style={{ color: "#b26a00" }}>{fmtMoney(s.apurada)}</Typography>
                            <Typography variant="caption" color="textSecondary">já pago: {fmtMoney(s.paga)}</Typography>
                          </div>
                        ))}
                      </Box>
                    )}

                  <Typography variant="subtitle2" gutterBottom>Regras de comissão</Typography>
                  {commRules.length === 0 ? <Typography variant="body2" color="textSecondary" paragraph>Nenhuma regra. Clique em "Nova Regra".</Typography>
                    : (
                      <div style={{ overflowX: "auto", width: "100%" }}>
                      <Table size="small" style={{ marginBottom: 16 }}>
                        <TableHead><TableRow>
                          <TableCell>Profissional</TableCell><TableCell align="right">Comissão</TableCell>
                          <TableCell align="center">Status</TableCell><TableCell align="center" style={{ width: 90 }}>Ações</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                          {commRules.map((r) => (
                            <TableRow key={r.id} hover>
                              <TableCell>{r.professionalName || `#${r.professionalId}`}</TableCell>
                              <TableCell align="right">{Number(r.percent)}%</TableCell>
                              <TableCell align="center">{r.active ? "Ativa" : "Inativa"}</TableCell>
                              <TableCell align="center">
                                <Tooltip title="Editar"><IconButton size="small" onClick={() => { setCommRuleEdit(r); setCommRuleOpen(true); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleDeleteRule(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                    )}

                  <Typography variant="subtitle2" gutterBottom>Comissões apuradas</Typography>
                  {commList.length === 0 ? <div className={classes.emptyState}><Typography>Nenhuma comissão no período. Defina a regra do profissional e registre pagamentos — ou clique em "Reapurar".</Typography></div>
                    : (
                      <div style={{ overflowX: "auto", width: "100%" }}>
                      <Table size="small" style={{ minWidth: 640 }}>
                        <TableHead><TableRow>
                          <TableCell>Data</TableCell><TableCell>Profissional</TableCell><TableCell>Paciente</TableCell>
                          <TableCell align="right">Base</TableCell><TableCell align="right">%</TableCell>
                          <TableCell align="right">Comissão</TableCell><TableCell align="center">Status</TableCell>
                          <TableCell align="center" style={{ width: 70 }}>Ações</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                          {commList.map((c) => (
                            <TableRow key={c.id} hover>
                              <TableCell>{formatDateBR(c.createdAt)}</TableCell>
                              <TableCell>{c.professionalName || `#${c.professionalId}`}</TableCell>
                              <TableCell>{c.patientName || "—"}</TableCell>
                              <TableCell align="right">{fmtMoney(c.baseAmount)}</TableCell>
                              <TableCell align="right">{Number(c.percent)}%</TableCell>
                              <TableCell align="right">{fmtMoney(c.amount)}</TableCell>
                              <TableCell align="center"><StatusChip map={COMMISSION_STATUS} value={c.status} /></TableCell>
                              <TableCell align="center">
                                {c.status === "apurada" && (
                                  <Tooltip title="Marcar como paga ao profissional"><IconButton size="small" color="primary" onClick={() => handlePayCommission(c)}><DoneAllIcon fontSize="small" /></IconButton></Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                    )}
                </>
              )}
          </>
        )}

        {/* TAB 7 — Contas (Conta Corrente) */}
        {tab === 7 && (
          <>
            {accounts.length > 0 && (
              <div className={classes.summary}>
                <div className={classes.card}>
                  <Typography variant="caption" color="textSecondary">Saldo total (contas ativas)</Typography>
                  <Typography className={classes.cardValue}>
                    {fmtMoney(accounts.filter((a) => a.active).reduce((s, a) => s + Number(a.currentBalance || 0), 0))}
                  </Typography>
                </div>
              </div>
            )}
            {accountsLoading ? <div className={classes.loadingWrap}><CircularProgress /></div>
              : accounts.length === 0 ? <div className={classes.emptyState}><Typography>Nenhuma conta cadastrada. Clique em "Nova Conta" (ex.: Caixa, banco, maquininha).</Typography></div>
              : (
                <div style={{ overflowX: "auto", width: "100%" }}>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Conta</TableCell><TableCell>Tipo</TableCell>
                    <TableCell align="right">Saldo atual</TableCell><TableCell align="center" style={{ width: 160 }}>Ações</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {accounts.map((a) => (
                      <TableRow key={a.id} hover>
                        <TableCell>
                          {a.name}
                          {a.isDefault && <Chip size="small" label="padrão" style={{ marginLeft: 6, backgroundColor: "#1565c0", color: "#fff" }} />}
                          {!a.active && <Chip size="small" label="inativa" style={{ marginLeft: 6 }} />}
                        </TableCell>
                        <TableCell>{accTypeLabel(a.type)}</TableCell>
                        <TableCell align="right" style={{ fontWeight: 700, color: Number(a.currentBalance) < 0 ? "#c62828" : undefined }}>{fmtMoney(a.currentBalance)}</TableCell>
                        <TableCell align="center">
                          <Button size="small" onClick={() => { setExtratoAccountId(String(a.id)); setTab(8); }}>Extrato</Button>
                          <Tooltip title="Editar"><IconButton size="small" onClick={() => { setAccountEdit(a); setAccountDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
          </>
        )}

        {/* TAB 8 — Extrato (livro-razão por conta) */}
        {tab === 8 && (
          <>
            <div className={classes.toolbar}>
              <TextField select size="small" variant="outlined" label="Conta" value={extratoAccountId}
                onChange={(e) => setExtratoAccountId(e.target.value)} style={{ minWidth: 200 }}>
                {accounts.map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>)}
              </TextField>
              <TextField type="date" size="small" variant="outlined" label="De" value={extFrom}
                onChange={(e) => setExtFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField type="date" size="small" variant="outlined" label="Até" value={extTo}
                onChange={(e) => setExtTo(e.target.value)} InputLabelProps={{ shrink: true }} />
              <Button size="small" variant="outlined" onClick={() => fetchEntries()}>Filtrar</Button>
              <Box flexGrow={1} />
              {extrato && <Typography variant="body2" color="textSecondary">Saldo atual: <strong>{fmtMoney(extrato.account?.currentBalance)}</strong></Typography>}
            </div>
            {extLoading ? <div className={classes.loadingWrap}><CircularProgress /></div>
              : !extrato ? <div className={classes.emptyState}><Typography>Selecione uma conta pra ver o extrato.</Typography></div>
              : (
                <>
                  <div className={classes.summary}>
                    <div className={classes.card}>
                      <Typography variant="caption" color="textSecondary">Saldo anterior</Typography>
                      <Typography className={classes.cardValue}>{fmtMoney(extrato.balanceBefore)}</Typography>
                    </div>
                    <div className={classes.card}>
                      <Typography variant="caption" color="textSecondary">Entradas</Typography>
                      <Typography className={classes.cardValue} style={{ color: "#2e7d32" }}>{fmtMoney(extrato.totalEntradas)}</Typography>
                    </div>
                    <div className={classes.card}>
                      <Typography variant="caption" color="textSecondary">Saídas</Typography>
                      <Typography className={classes.cardValue} style={{ color: "#c62828" }}>{fmtMoney(extrato.totalSaidas)}</Typography>
                    </div>
                    <div className={classes.card}>
                      <Typography variant="caption" color="textSecondary">Saldo no fim</Typography>
                      <Typography className={classes.cardValue} style={{ color: extrato.balanceEnd < 0 ? "#c62828" : undefined }}>{fmtMoney(extrato.balanceEnd)}</Typography>
                    </div>
                  </div>
                  {extrato.entries.length === 0 ? <div className={classes.emptyState}><Typography>Nenhum lançamento no período.</Typography></div>
                    : (
                      <div style={{ overflowX: "auto", width: "100%" }}>
                      <Table size="small" style={{ minWidth: 640 }}>
                        <TableHead><TableRow>
                          <TableCell>Data</TableCell><TableCell>Descrição</TableCell><TableCell>Origem</TableCell>
                          <TableCell align="right">Entrada</TableCell><TableCell align="right">Saída</TableCell><TableCell align="right">Saldo</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                          {extrato.entries.map((e) => (
                            <TableRow key={e.id} hover>
                              <TableCell>{formatDateBR(e.entryDate)}</TableCell>
                              <TableCell>{e.description || "—"}</TableCell>
                              <TableCell>{SOURCE_LABEL[e.sourceType] || e.sourceType}</TableCell>
                              <TableCell align="right" style={{ color: "#2e7d32" }}>{e.type === "credito" ? fmtMoney(e.amount) : "—"}</TableCell>
                              <TableCell align="right" style={{ color: "#c62828" }}>{e.type === "debito" ? fmtMoney(e.amount) : "—"}</TableCell>
                              <TableCell align="right" style={{ fontWeight: 600 }}>{fmtMoney(e.balance)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                    )}
                </>
              )}
          </>
        )}

        {/* TAB 9 — Conciliação bancária */}
        {tab === 9 && (
          <ReconciliationTab accounts={accounts} onChanged={() => { fetchAccounts(); if (tab === 8) fetchEntries(); }} />
        )}

        {/* TAB 10 — Controle de Cartões */}
        {tab === 10 && (
          <CardsTab accounts={accounts} onChanged={() => { fetchAccounts(); if (tab === 8) fetchEntries(); }} />
        )}

        {/* TAB 11 — Comissão Instituto Melo (Pablo) — gated por company 3 */}
        {tab === 11 && isMelo && (
          <MeloCommissionTab />
        )}

        {/* TAB 12 — Painel da Carol (Caixa do dia + Metas + Relatório por pessoa) — gated company 3 */}
        {tab === 12 && isMelo && (
          <MeloPainelTab unitId={unitParam} />
        )}
      </Paper>

      {/* Diálogos */}
      <PaymentDialog receivable={payTarget} open={!!payTarget} onClose={() => setPayTarget(null)} onPaid={handlePaid} accounts={accounts} unitId={createUnitId} />

      {/* [S91] Recebimento rápido de balcão — 1 tela, 1 clique (avulso + comprovante). */}
      <QuickPaymentDialog
        open={quickPayOpen}
        onClose={() => setQuickPayOpen(false)}
        units={units}
        defaultUnitId={createUnitId}
        hasUnits={hasUnits}
        onSaved={() => {
          setQuickPayOpen(false);
          fetchSummary();
          fetchAccounts();
          if (tab === 4) fetchPayments(1);
          if (tab === 2) fetchCashflow();
        }}
      />
      {/* [S103] Corrigir pagamento lançado (valor/forma/parcelas/pagador/anexo). */}
      <EditPaymentDialog
        open={!!editPayTarget}
        payment={editPayTarget}
        onClose={() => setEditPayTarget(null)}
        onSaved={() => {
          setEditPayTarget(null);
          fetchPayments(payPage);
          fetchSummary();
          fetchAccounts();
          if (tab === 2) fetchCashflow();
        }}
      />
      <FiscalEmitentesDialog
        open={fiscalDlgOpen}
        onClose={() => setFiscalDlgOpen(false)}
        units={units}
        hasUnits={hasUnits}
        onSaved={() => { reloadNfEmitentes(); if (tab === 4) reloadNfNotes(); }}
      />

      <QuoteFormDialog open={quoteFormOpen} unitId={createUnitId} onClose={() => setQuoteFormOpen(false)}
        onSaved={() => { setQuoteFormOpen(false); setTab(1); fetchQuotes(1); fetchSummary(); }} />

      <ExpenseFormDialog open={expenseFormOpen} unitId={createUnitId} onClose={() => setExpenseFormOpen(false)}
        onSaved={() => { setExpenseFormOpen(false); setTab(3); fetchExpenses(1); fetchSummary(); }} />

      {/* Aprovar orçamento */}
      <Dialog open={!!approveTarget} onClose={() => setApproveTarget(null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Aprovar orçamento</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Total: <strong>{fmtMoney(approveTarget?.total)}</strong>. Gera as contas a receber.
          </Typography>
          <TextField type="number" label="Parcelas" value={installments} onChange={(e) => setInstallments(e.target.value)}
            fullWidth margin="dense" inputProps={{ min: 1, max: 60 }} />
          <TextField type="date" label="1º vencimento" value={firstDueDate} onChange={(e) => setFirstDueDate(e.target.value)}
            fullWidth margin="dense" InputLabelProps={{ shrink: true }} helperText="Em branco = hoje. Demais parcelas mês a mês." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveTarget(null)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleApprove}>Aprovar</Button>
        </DialogActions>
      </Dialog>

      {/* Pagar despesa */}
      <Dialog open={!!expPayTarget} onClose={() => setExpPayTarget(null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Pagar despesa</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {expPayTarget?.description || "Despesa"} — <strong>{fmtMoney(expPayTarget?.amount)}</strong>
          </Typography>
          <TextField select label="Forma de pagamento" value={expPayMethod} onChange={(e) => setExpPayMethod(e.target.value)} fullWidth margin="dense">
            {EXPENSE_METHODS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </TextField>
          {accounts.filter((a) => a.active).length > 0 && (
            <TextField select label="Conta (de onde sai)" value={expPayAccountId} onChange={(e) => setExpPayAccountId(e.target.value)} fullWidth margin="dense">
              {accounts.filter((a) => a.active).map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>)}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpPayTarget(null)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handlePayExpense}>Confirmar pagamento</Button>
        </DialogActions>
      </Dialog>

      {/* Estoque (F2-ext) */}
      <StockProductDialog open={stockProductOpen} product={stockProductEdit} unitId={createUnitId}
        onClose={() => setStockProductOpen(false)}
        onSaved={() => { setStockProductOpen(false); fetchStock(); }} />
      <StockMovementDialog open={!!stockMoveTarget} product={stockMoveTarget}
        onClose={() => setStockMoveTarget(null)}
        onSaved={() => { setStockMoveTarget(null); fetchStock(); }} />

      {/* Comissões (F2-ext) */}
      <CommissionRuleDialog open={commRuleOpen} rule={commRuleEdit} professionals={commProfessionals}
        onClose={() => setCommRuleOpen(false)}
        onSaved={() => { setCommRuleOpen(false); fetchCommissions(); }} />

      {/* Tesouraria (S63) */}
      <AccountDialog open={accountDialogOpen} account={accountEdit} unitId={createUnitId}
        onClose={() => setAccountDialogOpen(false)}
        onSaved={() => { setAccountDialogOpen(false); fetchAccounts(); if (tab === 8) fetchEntries(); }} />
      <TransferDialog open={transferOpen} accounts={accounts}
        onClose={() => setTransferOpen(false)}
        onSaved={() => { setTransferOpen(false); fetchAccounts(); if (tab === 8) fetchEntries(); }} />
      <AccountEntryDialog open={entryDialogOpen} accounts={accounts} accountId={extratoAccountId}
        onClose={() => setEntryDialogOpen(false)}
        onSaved={() => { setEntryDialogOpen(false); fetchAccounts(); if (tab === 8) fetchEntries(); }} />
    </MainContainer>
  );
};

export default FinanceiroClinica;
